import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { diamondsApi, ordersApi } from '../services/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Textarea } from '../components/ui/textarea';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '../components/ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../components/ui/tabs';
import {
  Plus, Loader2, Diamond, AlertTriangle, TrendingDown, TrendingUp, Trash2, Pencil,
  Split, Send, Sliders, Package, Layers, Activity, Sparkles, RefreshCw, Search, ChevronUp, ChevronDown, GripVertical,
} from 'lucide-react';
import { toast } from 'sonner';
import { cn } from '../lib/utils';
import Header from '../components/layout/Header';
import { useAuth } from '../context/AuthContext';

const fmt = (n) => Number(n || 0).toFixed(3);
const parseCarat = (v) => {
  const n = parseFloat(v);
  return Number.isFinite(n) ? Math.round(n * 1000) / 1000 : 0;
};

const Diamonds = () => {
  const [tab, setTab] = useState('dashboard');
  const [shapes, setShapes] = useState([]);
  const [qualities, setQualities] = useState([]);
  const [sieves, setSieves] = useState([]);
  const [benchmarks, setBenchmarks] = useState([]);

  const loadConfig = useCallback(async () => {
    try {
      const [sh, q, sv, bm] = await Promise.all([
        diamondsApi.listShapes(), diamondsApi.listQualities(),
        diamondsApi.listSieves(), diamondsApi.listBenchmarks(),
      ]);
      setShapes(sh.data || []); setQualities(q.data || []);
      setSieves(sv.data || []); setBenchmarks(bm.data || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to load config');
    }
  }, []);
  useEffect(() => { loadConfig(); }, [loadConfig]);

  const shapeMap = useMemo(() => Object.fromEntries(shapes.map(s => [s.id, s])), [shapes]);
  const qualityMap = useMemo(() => Object.fromEntries(qualities.map(q => [q.id, q])), [qualities]);
  const sieveMap = useMemo(() => Object.fromEntries(sieves.map(s => [s.id, s])), [sieves]);

  return (
    <div className="min-h-screen bg-slate-50 dark:bg-neutral-950" data-testid="diamonds-page">
      <Header title="Diamond Inventory" />
      <div className="max-w-screen-2xl mx-auto px-3 md:px-8 py-4 md:py-6 space-y-4">
        <div className="flex items-center gap-3">
          <div className="h-11 w-11 rounded-xl bg-sky-100 dark:bg-sky-500/20 flex items-center justify-center">
            <Diamond className="h-5 w-5 text-sky-700 dark:text-sky-400" />
          </div>
          <div>
            <h2 className="text-lg md:text-xl font-bold tracking-tight">Diamond Inventory & Stock Register</h2>
            <p className="text-xs text-muted-foreground">Track unassorted lots, assorted buckets, and issue against orders — all in carats (3 decimals).</p>
          </div>
        </div>

        <Tabs value={tab} onValueChange={setTab}>
          <TabsList className="grid grid-cols-5 w-full max-w-xl h-auto">
            <TabsTrigger value="dashboard" className="py-2 text-xs" data-testid="tab-dashboard">Dashboard</TabsTrigger>
            <TabsTrigger value="unassorted" className="py-2 text-xs" data-testid="tab-unassorted">Unassorted</TabsTrigger>
            <TabsTrigger value="assorted" className="py-2 text-xs" data-testid="tab-assorted">Assorted</TabsTrigger>
            <TabsTrigger value="movements" className="py-2 text-xs" data-testid="tab-movements">Movements</TabsTrigger>
            <TabsTrigger value="settings" className="py-2 text-xs" data-testid="tab-settings">Settings</TabsTrigger>
          </TabsList>

          <TabsContent value="dashboard" className="mt-4">
            <DashboardTab {...{shapeMap, qualityMap, sieveMap}} />
          </TabsContent>
          <TabsContent value="unassorted" className="mt-4">
            <UnassortedTab {...{shapes, qualities, sieves, shapeMap, qualityMap, sieveMap}} />
          </TabsContent>
          <TabsContent value="assorted" className="mt-4">
            <AssortedTab {...{shapes, qualities, sieves, benchmarks, shapeMap, qualityMap, sieveMap}} />
          </TabsContent>
          <TabsContent value="movements" className="mt-4">
            <MovementsTab {...{shapes, qualities, sieves, shapeMap, qualityMap, sieveMap}} />
          </TabsContent>
          <TabsContent value="settings" className="mt-4">
            <SettingsTab {...{shapes, qualities, sieves, benchmarks, reload: loadConfig, shapeMap, qualityMap, sieveMap}} />
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
};

// ─── Dashboard ─────────────────────────────────────────────────────────────
const DashboardTab = ({ shapeMap, qualityMap, sieveMap }) => {
  const [d, setD] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try { const r = await diamondsApi.dashboard(); setD(r.data); }
    catch (e) { toast.error(e?.response?.data?.detail || 'Failed'); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  if (loading || !d) return <div className="flex justify-center py-16"><Loader2 className="h-6 w-6 animate-spin text-sky-600" /></div>;

  return (
    <div className="space-y-5" data-testid="diamonds-dashboard">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <KPI label="Total Carats" value={fmt(d.total_all)} sub={`${d.lot_count} lots · ${d.bucket_count} buckets`} tone="sky" testid="kpi-total"/>
        <KPI label="Unassorted" value={fmt(d.total_unassorted)} sub="pending sorting" tone="amber" testid="kpi-unassorted"/>
        <KPI label="Assorted" value={fmt(d.total_assorted)} sub="ready to issue" tone="emerald" testid="kpi-assorted"/>
        <KPI label="Warnings" value={d.warnings_count} sub="out-of-range buckets" tone={d.warnings_count ? "rose" : "slate"} testid="kpi-warnings"/>
      </div>

      {d.by_shape?.length > 0 && (
        <Card title="By shape">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
            {d.by_shape.map(row => (
              <div key={row.shape_id} className="p-3 rounded-lg border bg-white dark:bg-neutral-900">
                <p className="text-sm font-semibold">{shapeMap[row.shape_id]?.name || '—'}</p>
                <div className="mt-1 text-xs text-muted-foreground space-y-0.5">
                  <div className="flex justify-between"><span>Assorted</span><span className="font-mono text-emerald-600">{fmt(row.assorted)}</span></div>
                  <div className="flex justify-between"><span>Unassorted</span><span className="font-mono text-amber-600">{fmt(row.unassorted)}</span></div>
                </div>
              </div>
            ))}
          </div>
        </Card>
      )}

      {d.warnings?.length > 0 && (
        <Card title={<><AlertTriangle className="inline h-4 w-4 text-rose-600 mr-1" /> Benchmark alerts</>} testid="warnings-card">
          <div className="divide-y">
            {d.warnings.map((w, i) => (
              <div key={i} className={cn("py-2 flex items-center gap-3 flex-wrap", w.state === 'under' ? "text-rose-700" : "text-amber-700")}>
                <Badge variant="outline" className={cn("text-[10px]",
                  w.state === 'under' ? "border-rose-300 bg-rose-50" : "border-amber-300 bg-amber-50")}>
                  {w.state === 'under' ? <TrendingDown className="h-3 w-3 mr-1" /> : <TrendingUp className="h-3 w-3 mr-1" />}
                  {w.state === 'under' ? 'Below min' : 'Above max'}
                </Badge>
                <span className="text-sm font-medium">
                  {shapeMap[w.shape_id]?.name} · {qualityMap[w.quality_id]?.name} · {sieveMap[w.sieve_id]?.name}
                </span>
                <span className="text-xs ml-auto font-mono">
                  {fmt(w.current)} ct <span className="text-muted-foreground">(range {fmt(w.min)}–{fmt(w.max)})</span>
                </span>
              </div>
            ))}
          </div>
        </Card>
      )}

      {d.top_buckets?.length > 0 && (
        <Card title="Top buckets by weight">
          <div className="divide-y">
            {d.top_buckets.map(b => (
              <div key={b.id} className="py-2 flex items-center gap-2">
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium truncate">
                    {shapeMap[b.shape_id]?.name} · {qualityMap[b.quality_id]?.name} · {sieveMap[b.sieve_id]?.name}
                  </p>
                </div>
                <span className="text-sm font-mono font-semibold">{fmt(b.weight_carats)} ct</span>
              </div>
            ))}
          </div>
        </Card>
      )}
    </div>
  );
};

const KPI = ({ label, value, sub, tone, testid }) => {
  const toneCls = {
    sky: "bg-sky-50 dark:bg-sky-500/10 text-sky-700",
    amber: "bg-amber-50 dark:bg-amber-500/10 text-amber-700",
    emerald: "bg-emerald-50 dark:bg-emerald-500/10 text-emerald-700",
    rose: "bg-rose-50 dark:bg-rose-500/10 text-rose-700",
    slate: "bg-slate-50 dark:bg-neutral-800/50 text-slate-700",
  }[tone] || "";
  return (
    <div className={cn("p-4 rounded-xl border shadow-sm", toneCls)} data-testid={testid}>
      <p className="text-[10px] uppercase tracking-wider font-semibold opacity-80">{label}</p>
      <p className="mt-1 text-2xl font-bold tabular-nums">{value}</p>
      <p className="text-[10px] mt-1 opacity-60">{sub}</p>
    </div>
  );
};

const Card = ({ title, children, testid }) => (
  <div className="p-4 rounded-xl border bg-white dark:bg-neutral-900" data-testid={testid}>
    <div className="text-sm font-semibold mb-3">{title}</div>
    {children}
  </div>
);

// ─── Unassorted tab ────────────────────────────────────────────────────────
const UnassortedTab = ({ shapes, qualities, sieves, shapeMap, qualityMap, sieveMap }) => {
  const [lots, setLots] = useState([]);
  const [loading, setLoading] = useState(true);
  const [createOpen, setCreateOpen] = useState(false);
  const [assortOpen, setAssortOpen] = useState(null); // lot id
  const [issueOpen, setIssueOpen] = useState(null); // lot id
  const [historyOpen, setHistoryOpen] = useState(null); // lot id
  const [statusFilter, setStatusFilter] = useState('all');

  const load = useCallback(async () => {
    setLoading(true);
    try { const r = await diamondsApi.listLots(statusFilter === 'all' ? {} : { status: statusFilter }); setLots(r.data || []); }
    catch (e) { toast.error(e?.response?.data?.detail || 'Failed'); }
    finally { setLoading(false); }
  }, [statusFilter]);
  useEffect(() => { load(); }, [load]);

  const del = async (lot) => {
    if (!window.confirm(`Delete lot ${lot.packet_no || lot.id.slice(0,6)}? (only if untouched)`)) return;
    try { await diamondsApi.deleteLot(lot.id); toast.success('Deleted'); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || 'Failed'); }
  };

  return (
    <div className="space-y-3">
      <div className="flex items-center gap-2 flex-wrap">
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="w-44 h-9 text-xs" data-testid="unassorted-status-filter"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All lots</SelectItem>
            <SelectItem value="unassorted">Fresh (unassorted)</SelectItem>
            <SelectItem value="partially_assorted">Partially assorted</SelectItem>
            <SelectItem value="fully_assorted">Fully assorted</SelectItem>
            <SelectItem value="exhausted">Exhausted</SelectItem>
          </SelectContent>
        </Select>
        <div className="ml-auto flex gap-2">
          <Button size="sm" variant="outline" onClick={load} className="h-9" data-testid="reload-lots"><RefreshCw className="h-4 w-4" /></Button>
          <Button size="sm" onClick={() => setCreateOpen(true)} className="h-9 bg-sky-600 hover:bg-sky-700 text-white" data-testid="new-lot-btn">
            <Plus className="h-4 w-4 mr-1.5" /> New Packet
          </Button>
        </div>
      </div>

      {loading ? <div className="flex justify-center py-14"><Loader2 className="h-5 w-5 animate-spin text-sky-600" /></div> :
       lots.length === 0 ? <div className="py-14 text-center text-sm text-muted-foreground border border-dashed rounded-xl">No lots</div> :
       <div className="rounded-xl border bg-white dark:bg-neutral-900 overflow-hidden divide-y">
         {lots.map(lot => {
           const remaining = Number(lot.remaining_weight_carats || 0);
           const pct = lot.gross_weight_carats ? Math.round((1 - remaining / lot.gross_weight_carats) * 100) : 0;
           return (
             <div key={lot.id} className="px-3 md:px-5 py-3 flex items-center gap-3 flex-wrap sm:flex-nowrap" data-testid={`lot-${lot.id}`}>
               <button type="button" onClick={() => setHistoryOpen(lot.id)}
                        className="flex-1 min-w-0 text-left group cursor-pointer rounded-lg -mx-2 px-2 py-1 hover:bg-sky-50/70 dark:hover:bg-sky-500/5 transition-colors"
                        title="Click to view movement history"
                        data-testid={`lot-open-${lot.id}`}>
                 <div className="flex items-center gap-1.5 flex-wrap">
                   <div className="h-6 w-6 rounded-md bg-sky-100 dark:bg-sky-500/20 flex items-center justify-center flex-shrink-0">
                     <Package className="h-3.5 w-3.5 text-sky-700 dark:text-sky-400" />
                   </div>
                   <p className="text-sm font-semibold group-hover:text-sky-800">{lot.packet_no || `Lot ${lot.id.slice(0,6)}`}</p>
                   <Badge variant="outline" className={cn("text-[10px]",
                     lot.status === 'fully_assorted' && "border-emerald-300 bg-emerald-50 text-emerald-700",
                     lot.status === 'partially_assorted' && "border-amber-300 bg-amber-50 text-amber-700",
                     lot.status === 'exhausted' && "border-slate-300 bg-slate-50 text-slate-600",
                   )}>{lot.status.replace('_', ' ')}</Badge>
                 </div>
                 <p className="text-[11px] text-muted-foreground mt-0.5">
                   {shapeMap[lot.shape_id]?.name || '?'} · {qualityMap[lot.quality_id]?.name || '?'} · sieve {sieveMap[lot.sieve_id]?.name || '?'}
                   {lot.vendor_name && ` · ${lot.vendor_name}`}
                 </p>
                 <div className="mt-1 flex items-center gap-2">
                   <div className="h-1.5 rounded-full bg-slate-100 dark:bg-neutral-800 flex-1 max-w-xs overflow-hidden">
                     <div className="h-full bg-emerald-500 transition-all" style={{ width: `${pct}%` }} />
                   </div>
                   <p className="text-[11px] font-mono">{fmt(remaining)} / {fmt(lot.gross_weight_carats)} ct</p>
                 </div>
               </button>
               <div className="flex items-center gap-1 flex-shrink-0">
                 {remaining > 0 && (
                   <>
                     <Button size="sm" variant="outline" onClick={() => setAssortOpen(lot.id)} className="h-8 gap-1" data-testid={`assort-${lot.id}`}>
                       <Split className="h-3.5 w-3.5" /> <span className="hidden sm:inline text-xs">Assort</span>
                     </Button>
                     <Button size="sm" variant="outline" onClick={() => setIssueOpen(lot.id)} className="h-8 gap-1" data-testid={`issue-lot-${lot.id}`}>
                       <Send className="h-3.5 w-3.5" /> <span className="hidden sm:inline text-xs">Issue</span>
                     </Button>
                   </>
                 )}
                 <Button size="sm" variant="ghost" onClick={() => del(lot)} className="h-8 px-2 text-rose-600 hover:bg-rose-50 disabled:opacity-40" data-testid={`del-lot-${lot.id}`} disabled={remaining !== Number(lot.gross_weight_carats || 0)} title={remaining !== Number(lot.gross_weight_carats || 0) ? "Cannot delete — lot has been touched" : "Delete lot"}>
                   <Trash2 className="h-3.5 w-3.5" />
                 </Button>
               </div>
             </div>
           );
         })}
       </div>
      }

      {createOpen && <LotCreateDialog {...{shapes, qualities, sieves}} onClose={() => { setCreateOpen(false); load(); }} />}
      {assortOpen && <AssortDialog lotId={assortOpen} lot={lots.find(x => x.id === assortOpen)}
                                     {...{shapes, qualities, sieves, shapeMap, qualityMap, sieveMap}}
                                     onClose={() => { setAssortOpen(null); load(); }} />}
      {issueOpen && <LotIssueDialog lotId={issueOpen} lot={lots.find(x => x.id === issueOpen)}
                                      {...{qualities, sieves, shapeMap, qualityMap, sieveMap}}
                                      onClose={() => { setIssueOpen(null); load(); }} />}
      {historyOpen && <LotHistoryDialog lotId={historyOpen} lot={lots.find(x => x.id === historyOpen)}
                                          {...{shapeMap, qualityMap, sieveMap}}
                                          onClose={() => setHistoryOpen(null)} />}
    </div>
  );
};

const LotCreateDialog = ({ shapes, qualities, sieves, onClose }) => {
  const [f, setF] = useState({
    packet_no: '', vendor_name: '', shape_id: '', quality_id: '', sieve_id: '',
    gross_weight_carats: '', purchase_date: new Date().toISOString().slice(0,10), invoice_no: '', notes: '',
  });
  const [busy, setBusy] = useState(false);
  const activeShapes = shapes.filter(s => s.active !== false);
  const activeQ = qualities.filter(q => q.active !== false);
  const unassSieves = sieves.filter(s => s.shape_id === f.shape_id && s.kind === 'unassorted' && s.active !== false);
  const submit = async () => {
    if (!f.shape_id || !f.quality_id || !f.sieve_id) { toast.error('Shape, quality & sieve are required'); return; }
    const w = parseCarat(f.gross_weight_carats);
    if (w <= 0) { toast.error('Weight must be > 0'); return; }
    setBusy(true);
    try {
      await diamondsApi.createLot({ ...f, gross_weight_carats: w });
      toast.success('Lot added'); onClose();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Failed'); }
    finally { setBusy(false); }
  };
  return (
    <Dialog open onOpenChange={(v) => !busy && !v && onClose()}>
      <DialogContent className="max-w-lg" data-testid="lot-create-dialog">
        <DialogHeader><DialogTitle>Receive a new packet</DialogTitle>
        <DialogDescription>Unassorted lot straight from the vendor.</DialogDescription></DialogHeader>
        <div className="space-y-3 py-1">
          <div className="grid grid-cols-2 gap-2">
            <div><Label>Packet no.</Label><Input value={f.packet_no} onChange={e => setF(p => ({...p, packet_no: e.target.value}))} className="h-10 mt-1" data-testid="lot-packet-no" /></div>
            <div><Label>Vendor</Label><Input value={f.vendor_name} onChange={e => setF(p => ({...p, vendor_name: e.target.value}))} className="h-10 mt-1" data-testid="lot-vendor" /></div>
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div><Label>Shape *</Label>
              <Select value={f.shape_id} onValueChange={v => setF(p => ({...p, shape_id: v, sieve_id: ''}))}>
                <SelectTrigger className="h-10 mt-1" data-testid="lot-shape"><SelectValue placeholder="Pick shape" /></SelectTrigger>
                <SelectContent>{activeShapes.map(s => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><Label>Quality *</Label>
              <Select value={f.quality_id} onValueChange={v => setF(p => ({...p, quality_id: v}))}>
                <SelectTrigger className="h-10 mt-1" data-testid="lot-quality"><SelectValue placeholder="Pick quality" /></SelectTrigger>
                <SelectContent>{activeQ.map(s => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div><Label>Sieve group *</Label>
              <Select value={f.sieve_id} onValueChange={v => setF(p => ({...p, sieve_id: v}))} disabled={!f.shape_id}>
                <SelectTrigger className="h-10 mt-1" data-testid="lot-sieve"><SelectValue placeholder={f.shape_id ? 'Pick sieve group' : 'Pick shape first'} /></SelectTrigger>
                <SelectContent>{unassSieves.map(s => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}</SelectContent>
              </Select>
              {f.shape_id && unassSieves.length === 0 && <p className="text-[10px] text-rose-600 mt-1">No unassorted sieves configured for this shape.</p>}
            </div>
            <div><Label>Gross weight (ct) *</Label><Input type="number" step="0.001" value={f.gross_weight_carats} onChange={e => setF(p => ({...p, gross_weight_carats: e.target.value}))} className="h-10 mt-1 font-mono" placeholder="0.000" data-testid="lot-weight" /></div>
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div><Label>Purchase date</Label><Input type="date" value={f.purchase_date} onChange={e => setF(p => ({...p, purchase_date: e.target.value}))} className="h-10 mt-1" data-testid="lot-date" /></div>
            <div><Label>Invoice</Label><Input value={f.invoice_no} onChange={e => setF(p => ({...p, invoice_no: e.target.value}))} className="h-10 mt-1" data-testid="lot-invoice" /></div>
          </div>
          <div><Label>Notes</Label><Textarea value={f.notes} onChange={e => setF(p => ({...p, notes: e.target.value}))} rows={2} className="mt-1" /></div>
        </div>
        <DialogFooter><Button variant="outline" onClick={onClose} disabled={busy}>Cancel</Button>
        <Button onClick={submit} disabled={busy} className="bg-sky-600 hover:bg-sky-700 text-white" data-testid="lot-submit">
          {busy ? <Loader2 className="h-4 w-4 animate-spin mr-1.5" /> : <Plus className="h-4 w-4 mr-1.5" />}Add Packet</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

const AssortDialog = ({ lotId, lot, shapes, qualities, sieves, shapeMap, qualityMap, sieveMap, onClose }) => {
  // Lot's shape is fixed — bulk-assort is scoped to that shape.
  const lotShapeId = lot?.shape_id || '';
  const assSieves = useMemo(
    () => sieves.filter(s => s.shape_id === lotShapeId && s.kind === 'assorted' && s.active !== false)
                 .sort((a, b) => (a.sort_order || 0) - (b.sort_order || 0)),
    [sieves, lotShapeId]
  );
  const activeQualities = useMemo(() => qualities.filter(q => q.active !== false), [qualities]);

  // Rows: each has { quality_id, sieve_id, weight_carats }
  const emptyRow = () => ({ key: Math.random().toString(36).slice(2), quality_id: '', sieve_id: '', weight_carats: '' });
  const [rows, setRows] = useState([emptyRow(), emptyRow(), emptyRow()]);
  const [notes, setNotes] = useState('');
  const [busy, setBusy] = useState(false);

  const remaining = Number(lot?.remaining_weight_carats || 0);
  const totalTyped = rows.reduce((sum, r) => sum + (parseCarat(r.weight_carats) || 0), 0);
  const roundedTotal = Math.round(totalTyped * 1000) / 1000;
  const balance = Math.round((remaining - roundedTotal) * 1000) / 1000;
  const overBudget = roundedTotal > remaining + 0.0005;

  const updateRow = (key, patch) =>
    setRows(rs => rs.map(r => r.key === key ? { ...r, ...patch } : r));
  const removeRow = (key) =>
    setRows(rs => (rs.length <= 1 ? rs : rs.filter(r => r.key !== key)));
  const addRow = () => setRows(rs => [...rs, emptyRow()]);
  const fillRemaining = (key) => {
    // Set this row's weight = current balance (rounded), so total = remaining
    const other = rows.filter(r => r.key !== key).reduce((s, r) => s + (parseCarat(r.weight_carats) || 0), 0);
    const w = Math.max(0, Math.round((remaining - other) * 1000) / 1000);
    updateRow(key, { weight_carats: w ? String(w) : '' });
  };

  const submit = async () => {
    const entries = rows
      .filter(r => r.quality_id && r.sieve_id && parseCarat(r.weight_carats) > 0)
      .map(r => ({
        shape_id: lotShapeId,
        quality_id: r.quality_id,
        sieve_id: r.sieve_id,
        weight_carats: parseCarat(r.weight_carats),
      }));
    if (entries.length === 0) { toast.error('Add at least one row with quality, sieve and weight'); return; }
    // Row-level completeness check
    for (const r of rows) {
      const w = parseCarat(r.weight_carats);
      if (w > 0 && (!r.quality_id || !r.sieve_id)) {
        toast.error('Every row with a weight needs quality and sieve'); return;
      }
    }
    if (overBudget) { toast.error(`Total ${roundedTotal.toFixed(3)} ct exceeds remaining ${remaining.toFixed(3)} ct`); return; }
    setBusy(true);
    try {
      await diamondsApi.assortLotBulk(lotId, { entries, notes });
      toast.success(`Assorted ${entries.length} bucket${entries.length === 1 ? '' : 's'} · ${roundedTotal.toFixed(3)} ct`);
      onClose();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Failed'); }
    finally { setBusy(false); }
  };

  const balanceTone = balance < -0.0005 ? "text-rose-700"
                      : Math.abs(balance) <= 0.0005 ? "text-emerald-700"
                      : "text-amber-700";

  return (
    <Dialog open onOpenChange={(v) => !busy && !v && onClose()}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="assort-dialog">
        <DialogHeader>
          <DialogTitle>Bulk assort — {lot?.packet_no || 'lot'}</DialogTitle>
          <DialogDescription>
            {shapeMap[lotShapeId]?.name} · Split this packet across as many quality × sieve buckets as needed. Everything applies in one shot.
          </DialogDescription>
        </DialogHeader>

        <div className="grid grid-cols-3 gap-2 text-xs">
          <div className="p-2 rounded-lg bg-slate-50 dark:bg-neutral-800/50">
            <p className="opacity-60 text-[10px] uppercase tracking-wide">Remaining</p>
            <p className="font-mono text-sm font-semibold">{fmt(remaining)} ct</p>
          </div>
          <div className={cn("p-2 rounded-lg bg-slate-50 dark:bg-neutral-800/50", overBudget && "bg-rose-50 text-rose-700")}>
            <p className="opacity-60 text-[10px] uppercase tracking-wide">Total typed</p>
            <p className="font-mono text-sm font-semibold" data-testid="assort-total">{fmt(roundedTotal)} ct</p>
          </div>
          <div className={cn("p-2 rounded-lg bg-slate-50 dark:bg-neutral-800/50", balanceTone)}>
            <p className="opacity-60 text-[10px] uppercase tracking-wide">Balance after</p>
            <p className="font-mono text-sm font-semibold" data-testid="assort-balance">{fmt(balance)} ct</p>
          </div>
        </div>

        {assSieves.length === 0 && (
          <div className="p-3 text-xs text-rose-700 bg-rose-50 border border-rose-200 rounded-lg">
            No assorted sieves configured for {shapeMap[lotShapeId]?.name}. Add them in Settings first.
          </div>
        )}

        <div className="rounded-xl border overflow-hidden">
          <div className="grid grid-cols-12 gap-2 px-3 py-2 bg-slate-50 dark:bg-neutral-800/50 text-[10px] font-semibold uppercase tracking-wide opacity-70">
            <div className="col-span-4">Quality</div>
            <div className="col-span-4">Sieve</div>
            <div className="col-span-3">Weight (ct)</div>
            <div className="col-span-1"></div>
          </div>
          <div className="divide-y">
            {rows.map((r, idx) => (
              <div key={r.key} className="grid grid-cols-12 gap-2 px-3 py-2 items-center" data-testid={`assort-row-${idx}`}>
                <div className="col-span-4">
                  <Select value={r.quality_id} onValueChange={v => updateRow(r.key, { quality_id: v })}>
                    <SelectTrigger className="h-9 text-xs" data-testid={`assort-row-quality-${idx}`}><SelectValue placeholder="Pick" /></SelectTrigger>
                    <SelectContent>{activeQualities.map(q => <SelectItem key={q.id} value={q.id}>{q.name}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div className="col-span-4">
                  <Select value={r.sieve_id} onValueChange={v => updateRow(r.key, { sieve_id: v })}>
                    <SelectTrigger className="h-9 text-xs" data-testid={`assort-row-sieve-${idx}`}><SelectValue placeholder="Pick" /></SelectTrigger>
                    <SelectContent>{assSieves.map(s => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div className="col-span-3 flex gap-1">
                  <Input type="number" step="0.001" value={r.weight_carats}
                          onChange={e => updateRow(r.key, { weight_carats: e.target.value })}
                          className="h-9 text-xs font-mono" placeholder="0.000"
                          data-testid={`assort-row-weight-${idx}`} />
                  <button type="button" onClick={() => fillRemaining(r.key)}
                          className="text-[10px] px-1.5 rounded border border-sky-200 bg-sky-50 text-sky-700 hover:bg-sky-100 shrink-0"
                          title="Fill remaining balance into this row"
                          data-testid={`assort-row-fill-${idx}`}>
                    all
                  </button>
                </div>
                <div className="col-span-1 text-right">
                  <button type="button" onClick={() => removeRow(r.key)} disabled={rows.length <= 1}
                          className="h-7 w-7 inline-flex items-center justify-center text-rose-600 hover:bg-rose-50 rounded disabled:opacity-30 disabled:cursor-not-allowed"
                          data-testid={`assort-row-del-${idx}`}>
                    <Trash2 className="h-3.5 w-3.5" />
                  </button>
                </div>
              </div>
            ))}
          </div>
          <div className="px-3 py-2 border-t bg-slate-50/60 dark:bg-neutral-800/40">
            <Button size="sm" variant="ghost" onClick={addRow} className="h-7 text-xs" data-testid="assort-add-row">
              <Plus className="h-3.5 w-3.5 mr-1" /> Add another row
            </Button>
          </div>
        </div>

        <div><Label className="text-xs">Notes (optional — applied to all rows)</Label>
          <Textarea value={notes} onChange={e => setNotes(e.target.value)} rows={2} className="mt-1 text-xs" data-testid="assort-notes" />
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={busy}>Cancel</Button>
          <Button onClick={submit} disabled={busy || overBudget || roundedTotal <= 0}
                  className="bg-sky-600 hover:bg-sky-700 text-white" data-testid="assort-submit">
            {busy ? <Loader2 className="h-4 w-4 animate-spin mr-1.5" /> : <Split className="h-4 w-4 mr-1.5" />}
            Move {fmt(roundedTotal)} ct to assorted
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

const LotIssueDialog = ({ lotId, lot, qualities = [], sieves = [], shapeMap, qualityMap, sieveMap, onClose }) => {
  const [f, setF] = useState({
    weight_carats: '', reference: '', order_id: '', notes: '',
    quality_id: lot?.quality_id || '', sieve_id: '',
  });
  const [orders, setOrders] = useState([]);
  const [busy, setBusy] = useState(false);
  useEffect(() => { ordersApi.getAll?.().then(r => setOrders(r.data || [])).catch(() => {}); }, []);
  // Sieves restricted to this lot's shape + assorted kind (same list the assorted matrix uses)
  const assSieves = useMemo(
    () => sieves.filter(s => s.shape_id === lot?.shape_id && s.kind === 'assorted' && s.active !== false)
                 .sort((a, b) => (a.sort_order || 0) - (b.sort_order || 0)),
    [sieves, lot?.shape_id]
  );
  const activeQ = useMemo(() => qualities.filter(q => q.active !== false), [qualities]);
  const submit = async () => {
    const w = parseCarat(f.weight_carats);
    if (w <= 0) { toast.error('Weight > 0'); return; }
    setBusy(true);
    try {
      await diamondsApi.issueFromLot(lotId, {
        weight_carats: w,
        order_id: f.order_id || undefined,
        reference: f.reference || undefined,
        notes: f.notes || undefined,
        quality_id: f.quality_id || undefined,
        sieve_id: f.sieve_id || undefined,
      });
      toast.success('Issued'); onClose();
    }
    catch (e) { toast.error(e?.response?.data?.detail || 'Failed'); }
    finally { setBusy(false); }
  };
  return (
    <Dialog open onOpenChange={(v) => !busy && !v && onClose()}>
      <DialogContent className="max-w-md" data-testid="lot-issue-dialog">
        <DialogHeader><DialogTitle>Issue directly from {lot?.packet_no || 'lot'}</DialogTitle>
        <DialogDescription>Skip assorting — issue straight to an order. Optionally tag the quality & sieve so it shows up in the Assorted movement log.</DialogDescription></DialogHeader>
        <div className="text-xs text-muted-foreground bg-slate-50 dark:bg-neutral-800/50 p-2 rounded">
          {shapeMap[lot?.shape_id]?.name} · packet {qualityMap[lot?.quality_id]?.name} · sieve {sieveMap[lot?.sieve_id]?.name} · Remaining: <strong className="font-mono">{fmt(lot?.remaining_weight_carats)} ct</strong>
        </div>
        <div className="space-y-3">
          <div className="grid grid-cols-2 gap-2">
            <div><Label>Target quality</Label>
              <Select value={f.quality_id || 'lot'} onValueChange={v => setF(p => ({...p, quality_id: v === 'lot' ? '' : v }))}>
                <SelectTrigger className="h-10 mt-1" data-testid="lot-issue-quality"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="lot">(same as packet)</SelectItem>
                  {activeQ.map(q => <SelectItem key={q.id} value={q.id}>{q.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div><Label>Target sieve</Label>
              <Select value={f.sieve_id || 'lot'} onValueChange={v => setF(p => ({...p, sieve_id: v === 'lot' ? '' : v }))}>
                <SelectTrigger className="h-10 mt-1" data-testid="lot-issue-sieve"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="lot">(same as packet)</SelectItem>
                  {assSieves.map(s => <SelectItem key={s.id} value={s.id}>{s.name}{s.mm != null ? ` · ${s.mm} mm` : ''}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>
          <div><Label>Link to order</Label>
            <Select value={f.order_id || 'none'} onValueChange={v => setF(p => ({ ...p, order_id: v === 'none' ? '' : v }))}>
              <SelectTrigger className="h-10 mt-1" data-testid="lot-issue-order"><SelectValue placeholder="Pick order (optional)" /></SelectTrigger>
              <SelectContent><SelectItem value="none">(no order — use reference field)</SelectItem>
                {orders.slice(0, 200).map(o => <SelectItem key={o.id} value={o.id}>{[o.order_number, o.customer_company_name].filter(Boolean).join(' · ')}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div><Label>Or reference (free text)</Label><Input value={f.reference} onChange={e => setF(p => ({...p, reference: e.target.value}))} placeholder="e.g. Voucher #234" className="h-10 mt-1" data-testid="lot-issue-ref" /></div>
          <div><Label>Weight (ct)</Label><Input type="number" step="0.001" value={f.weight_carats} onChange={e => setF(p => ({...p, weight_carats: e.target.value}))} className="h-10 mt-1 font-mono" data-testid="lot-issue-weight" /></div>
          <div><Label>Notes</Label><Textarea value={f.notes} onChange={e => setF(p => ({...p, notes: e.target.value}))} rows={2} className="mt-1" /></div>
        </div>
        <DialogFooter><Button variant="outline" onClick={onClose} disabled={busy}>Cancel</Button>
        <Button onClick={submit} disabled={busy} className="bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="lot-issue-submit">
          {busy ? <Loader2 className="h-4 w-4 animate-spin mr-1.5" /> : <Send className="h-4 w-4 mr-1.5" />}Issue</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

// ─── Lot movement history ──────────────────────────────────────────────
const LotHistoryDialog = ({ lotId, lot, shapeMap, qualityMap, sieveMap, onClose }) => {
  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    let alive = true;
    setLoading(true);
    diamondsApi.getLot(lotId)
      .then(r => { if (alive) setDetail(r.data); })
      .catch(e => { if (alive) toast.error(e?.response?.data?.detail || 'Failed to load history'); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [lotId]);

  const typeChip = (t) => {
    if (t === 'purchase') return { label: 'Purchase', cls: 'border-sky-300 bg-sky-50 text-sky-700' };
    if (t === 'assort') return { label: 'Assort', cls: 'border-violet-300 bg-violet-50 text-violet-700' };
    if (t === 'issue') return { label: 'Issue', cls: 'border-emerald-300 bg-emerald-50 text-emerald-700' };
    return { label: 'Adjust', cls: 'border-amber-300 bg-amber-50 text-amber-700' };
  };

  const movements = detail?.movements || [];
  const assortedTotal = movements.filter(m => m.type === 'assort').reduce((s, m) => s + Number(m.weight_carats || 0), 0);
  const issuedTotal = movements.filter(m => m.type === 'issue').reduce((s, m) => s + Number(m.weight_carats || 0), 0);

  return (
    <Dialog open onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="lot-history-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Package className="h-5 w-5 text-sky-600" />
            {lot?.packet_no || 'Lot'} · movement history
          </DialogTitle>
          <DialogDescription>
            {shapeMap[lot?.shape_id]?.name} · {qualityMap[lot?.quality_id]?.name} · sieve {sieveMap[lot?.sieve_id]?.name}
            {lot?.vendor_name && ` · ${lot.vendor_name}`}
          </DialogDescription>
        </DialogHeader>

        <div className="grid grid-cols-4 gap-2 text-xs">
          <div className="p-2 rounded-lg bg-slate-50 dark:bg-neutral-800/50">
            <p className="opacity-60 text-[10px] uppercase tracking-wide">Gross</p>
            <p className="font-mono text-sm font-semibold">{fmt(lot?.gross_weight_carats || 0)} ct</p>
          </div>
          <div className="p-2 rounded-lg bg-violet-50 text-violet-700">
            <p className="opacity-60 text-[10px] uppercase tracking-wide">Assorted</p>
            <p className="font-mono text-sm font-semibold">{fmt(assortedTotal)} ct</p>
          </div>
          <div className="p-2 rounded-lg bg-emerald-50 text-emerald-700">
            <p className="opacity-60 text-[10px] uppercase tracking-wide">Issued</p>
            <p className="font-mono text-sm font-semibold">{fmt(issuedTotal)} ct</p>
          </div>
          <div className="p-2 rounded-lg bg-amber-50 text-amber-700">
            <p className="opacity-60 text-[10px] uppercase tracking-wide">Remaining</p>
            <p className="font-mono text-sm font-semibold">{fmt(lot?.remaining_weight_carats || 0)} ct</p>
          </div>
        </div>

        {loading ? (
          <div className="flex justify-center py-10"><Loader2 className="h-5 w-5 animate-spin text-sky-600" /></div>
        ) : movements.length === 0 ? (
          <div className="py-10 text-center text-sm text-muted-foreground border border-dashed rounded-xl">No movements yet</div>
        ) : (
          <div className="rounded-xl border divide-y">
            {movements.slice().reverse().map(m => {
              const chip = typeChip(m.type);
              return (
                <div key={m.id} className="px-3 py-2 flex items-center gap-2 flex-wrap sm:flex-nowrap" data-testid={`history-${m.id}`}>
                  <Badge variant="outline" className={cn("text-[10px] flex-shrink-0", chip.cls)}>{chip.label}</Badge>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm">
                      {qualityMap[m.quality_id]?.name || '?'} · {sieveMap[m.sieve_id]?.name || '?'}
                    </p>
                    <p className="text-[10px] text-muted-foreground truncate">
                      {new Date(m.created_at).toLocaleString()} · {m.created_by_name || 'system'}
                      {m.reference && ` · ${m.reference}`}
                      {m.notes && ` · ${m.notes}`}
                    </p>
                  </div>
                  <div className={cn("text-sm font-mono font-semibold flex-shrink-0",
                    m.direction === 'in' ? "text-emerald-600" : m.direction === 'out' ? "text-rose-600" : "text-violet-600")}>
                    {m.direction === 'out' ? '-' : m.direction === 'in' ? '+' : ''}{fmt(m.weight_carats)} ct
                  </div>
                </div>
              );
            })}
          </div>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Close</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

// ─── Assorted tab (matrix per shape) ───────────────────────────────────────
const AssortedTab = ({ shapes, qualities, sieves, benchmarks, shapeMap, qualityMap, sieveMap }) => {
  const [stock, setStock] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedShape, setSelectedShape] = useState('');
  const [qualityFilter, setQualityFilter] = useState('all');
  const [issueBucket, setIssueBucket] = useState(null);
  const [adjustBucket, setAdjustBucket] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try { const r = await diamondsApi.listStock(); setStock(r.data || []); }
    catch (e) { toast.error(e?.response?.data?.detail || 'Failed'); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  useEffect(() => { if (!selectedShape && shapes.length) setSelectedShape(shapes[0].id); }, [shapes, selectedShape]);

  const stockByKey = useMemo(() => {
    const m = {};
    stock.forEach(s => { m[`${s.shape_id}|${s.quality_id}|${s.sieve_id}`] = s; });
    return m;
  }, [stock]);
  const benchmarkByKey = useMemo(() => {
    const m = {};
    benchmarks.forEach(b => { m[`${b.shape_id}|${b.quality_id}|${b.sieve_id}`] = b; });
    return m;
  }, [benchmarks]);

  const shapeSieves = sieves.filter(s => s.shape_id === selectedShape && s.kind === 'assorted').sort((a,b) => (a.sort_order||0)-(b.sort_order||0));

  // Total carats per quality (across all sieves) for the currently-selected shape.
  const qualityTotals = useMemo(() => {
    const m = {};
    stock.filter(s => s.shape_id === selectedShape).forEach(s => {
      m[s.quality_id] = (m[s.quality_id] || 0) + (Number(s.weight_carats) || 0);
    });
    return m;
  }, [stock, selectedShape]);

  // Rows to render: apply the quality filter but keep row order stable.
  const visibleQualities = useMemo(() => (
    qualityFilter === 'all' ? qualities : qualities.filter(q => q.id === qualityFilter)
  ), [qualities, qualityFilter]);

  return (
    <div className="space-y-3">
      <div className="flex items-center gap-2 flex-wrap">
        <Select value={selectedShape} onValueChange={setSelectedShape}>
          <SelectTrigger className="w-56 h-9" data-testid="assorted-shape-pick"><SelectValue /></SelectTrigger>
          <SelectContent>{shapes.map(s => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}</SelectContent>
        </Select>
        <Select value={qualityFilter} onValueChange={setQualityFilter}>
          <SelectTrigger className="w-56 h-9 text-xs" data-testid="assorted-quality-filter"><SelectValue placeholder="All qualities" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All qualities</SelectItem>
            {qualities.map(q => <SelectItem key={q.id} value={q.id}>{q.name}</SelectItem>)}
          </SelectContent>
        </Select>
        <Button size="sm" variant="outline" onClick={load} className="h-9" data-testid="reload-stock"><RefreshCw className="h-4 w-4" /></Button>
      </div>
      {loading ? <div className="flex justify-center py-14"><Loader2 className="h-5 w-5 animate-spin text-sky-600" /></div> :
       shapeSieves.length === 0 ? <div className="py-14 text-center text-sm text-muted-foreground border border-dashed rounded-xl">No assorted sieves configured for this shape — set them in Settings.</div> :
       <div className="rounded-xl border bg-white dark:bg-neutral-900 overflow-x-auto" data-testid="assorted-matrix">
         <table className="w-full text-xs border-separate border-spacing-0">
           <thead className="bg-slate-50 dark:bg-neutral-800/50">
             <tr>
               <th className="sticky left-0 z-20 bg-slate-100 dark:bg-neutral-800 text-left px-3 py-2 font-semibold border-b shadow-[2px_0_0_rgba(0,0,0,0.04)]">Quality \ Sieve</th>
               {shapeSieves.map(sv => (
                 <th key={sv.id} className="px-2 py-2 font-semibold text-center border-b whitespace-nowrap">
                   <div>{sv.name}</div>
                   {sv.mm != null && <div className="text-[10px] font-normal opacity-70">{sv.mm} mm</div>}
                   {sv.weight_per_stone_carats && <div className="text-[10px] font-normal opacity-60">{fmt(sv.weight_per_stone_carats)}/pc</div>}
                 </th>
               ))}
             </tr>
           </thead>
           <tbody>
             {visibleQualities.map(q => (
               <tr key={q.id}>
                 <td className="sticky left-0 z-10 bg-white dark:bg-neutral-900 px-3 py-2 font-medium border-t border-slate-200 dark:border-neutral-800 shadow-[2px_0_0_rgba(0,0,0,0.04)] whitespace-nowrap">
                   <div>{q.name}</div>
                   <div className="text-[10px] font-mono text-sky-700 opacity-80" data-testid={`q-total-${q.id}`}>Σ {fmt(qualityTotals[q.id] || 0)} ct</div>
                 </td>
                 {shapeSieves.map(sv => {
                   const key = `${selectedShape}|${q.id}|${sv.id}`;
                   const s = stockByKey[key];
                   const b = benchmarkByKey[key];
                   const w = s ? Number(s.weight_carats) : 0;
                   const under = b && b.min_carats > 0 && w < b.min_carats;
                   const over = b && b.max_carats > 0 && w > b.max_carats;
                   return (
                     <td key={sv.id} className={cn("px-1 py-1 text-center border-t border-slate-200 dark:border-neutral-800",
                       under && "bg-rose-50", over && "bg-amber-50")}>
                       <button
                         type="button"
                         onClick={() => setIssueBucket({ shape_id: selectedShape, quality_id: q.id, sieve_id: sv.id, weight_carats: w })}
                         className="w-full py-1.5 rounded hover:bg-slate-100 dark:hover:bg-neutral-800 font-mono"
                         title={b ? `Range ${fmt(b.min_carats)}–${fmt(b.max_carats)}` : ''}
                         data-testid={`cell-${q.id}-${sv.id}`}
                       >
                         <div className={cn("text-sm font-semibold", under && "text-rose-700", over && "text-amber-700")}>{fmt(w)}</div>
                         {b && (b.min_carats > 0 || b.max_carats > 0) && (
                           <div className="text-[9px] opacity-70">{fmt(b.min_carats)}–{fmt(b.max_carats)}</div>
                         )}
                       </button>
                     </td>
                   );
                 })}
               </tr>
             ))}
           </tbody>
         </table>
       </div>
      }
      {issueBucket && <BucketActionDialog bucket={issueBucket} {...{shapeMap, qualityMap, sieveMap}}
                                          onClose={() => { setIssueBucket(null); load(); }}
                                          onSwitchAdjust={() => { setAdjustBucket(issueBucket); setIssueBucket(null); }} />}
      {adjustBucket && <BucketAdjustDialog bucket={adjustBucket} {...{shapeMap, qualityMap, sieveMap}}
                                            onClose={() => { setAdjustBucket(null); load(); }} />}
    </div>
  );
};

const BucketActionDialog = ({ bucket, shapeMap, qualityMap, sieveMap, onClose, onSwitchAdjust }) => {
  const [f, setF] = useState({ weight_carats: '', reference: '', order_id: '', notes: '' });
  const [orders, setOrders] = useState([]);
  const [busy, setBusy] = useState(false);
  useEffect(() => { ordersApi.getAll?.().then(r => setOrders(r.data || [])).catch(() => {}); }, []);
  const submit = async () => {
    const w = parseCarat(f.weight_carats);
    if (w <= 0) { toast.error('Weight > 0'); return; }
    if (w > bucket.weight_carats) { toast.error(`Only ${fmt(bucket.weight_carats)} ct in bucket`); return; }
    setBusy(true);
    try { await diamondsApi.issueFromStock({ ...bucket, ...f, weight_carats: w }); toast.success('Issued'); onClose(); }
    catch (e) { toast.error(e?.response?.data?.detail || 'Failed'); }
    finally { setBusy(false); }
  };
  return (
    <Dialog open onOpenChange={(v) => !busy && !v && onClose()}>
      <DialogContent className="max-w-md" data-testid="bucket-issue-dialog">
        <DialogHeader><DialogTitle>Issue from bucket</DialogTitle>
          <DialogDescription>{shapeMap[bucket.shape_id]?.name} · {qualityMap[bucket.quality_id]?.name} · {sieveMap[bucket.sieve_id]?.name}</DialogDescription>
        </DialogHeader>
        <div className="text-xs text-muted-foreground bg-slate-50 dark:bg-neutral-800/50 p-2 rounded">
          On hand: <strong className="font-mono">{fmt(bucket.weight_carats)} ct</strong>
        </div>
        <div className="space-y-3">
          <div><Label>Link to order</Label>
            <Select value={f.order_id || 'none'} onValueChange={v => setF(p => ({...p, order_id: v === 'none' ? '' : v}))}>
              <SelectTrigger className="h-10 mt-1" data-testid="bucket-issue-order"><SelectValue placeholder="Optional" /></SelectTrigger>
              <SelectContent><SelectItem value="none">(no order — use reference)</SelectItem>
                {orders.slice(0, 200).map(o => <SelectItem key={o.id} value={o.id}>{[o.order_number, o.customer_company_name].filter(Boolean).join(' · ')}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div><Label>Or reference (free text)</Label><Input value={f.reference} onChange={e => setF(p => ({...p, reference: e.target.value}))} className="h-10 mt-1" data-testid="bucket-issue-ref" /></div>
          <div><Label>Weight (ct)</Label><Input type="number" step="0.001" value={f.weight_carats} onChange={e => setF(p => ({...p, weight_carats: e.target.value}))} className="h-10 mt-1 font-mono" data-testid="bucket-issue-weight" /></div>
          <div><Label>Notes</Label><Textarea value={f.notes} onChange={e => setF(p => ({...p, notes: e.target.value}))} rows={2} className="mt-1" /></div>
          <Button size="sm" variant="ghost" onClick={onSwitchAdjust} className="text-xs text-amber-700" data-testid="switch-adjust">
            <Sliders className="h-3 w-3 mr-1" /> Adjust this bucket instead (correction)
          </Button>
        </div>
        <DialogFooter><Button variant="outline" onClick={onClose} disabled={busy}>Cancel</Button>
        <Button onClick={submit} disabled={busy} className="bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="bucket-issue-submit">
          {busy ? <Loader2 className="h-4 w-4 animate-spin mr-1.5" /> : <Send className="h-4 w-4 mr-1.5" />}Issue</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

const BucketAdjustDialog = ({ bucket, shapeMap, qualityMap, sieveMap, onClose }) => {
  const [f, setF] = useState({ delta_carats: '', notes: '' });
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    const d = parseCarat(f.delta_carats);
    if (d === 0) { toast.error('Delta can\'t be zero'); return; }
    if (!f.notes.trim()) { toast.error('Notes required for adjustments'); return; }
    setBusy(true);
    try { await diamondsApi.adjustStock({ ...bucket, delta_carats: d, notes: f.notes }); toast.success('Adjusted'); onClose(); }
    catch (e) { toast.error(e?.response?.data?.detail || 'Failed'); }
    finally { setBusy(false); }
  };
  return (
    <Dialog open onOpenChange={(v) => !busy && !v && onClose()}>
      <DialogContent className="max-w-md" data-testid="bucket-adjust-dialog">
        <DialogHeader><DialogTitle>Adjust bucket</DialogTitle>
          <DialogDescription>{shapeMap[bucket.shape_id]?.name} · {qualityMap[bucket.quality_id]?.name} · {sieveMap[bucket.sieve_id]?.name}</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div><Label>Delta carats (use negative for reduction)</Label>
            <Input type="number" step="0.001" value={f.delta_carats} onChange={e => setF(p => ({...p, delta_carats: e.target.value}))} className="h-10 mt-1 font-mono" placeholder="e.g. -0.500 or 1.200" data-testid="adjust-delta" /></div>
          <div><Label>Reason *</Label><Textarea value={f.notes} onChange={e => setF(p => ({...p, notes: e.target.value}))} rows={2} className="mt-1" data-testid="adjust-notes" placeholder="e.g. Physical count correction after audit" /></div>
        </div>
        <DialogFooter><Button variant="outline" onClick={onClose} disabled={busy}>Cancel</Button>
        <Button onClick={submit} disabled={busy} className="bg-amber-600 hover:bg-amber-700 text-white" data-testid="adjust-submit">
          {busy ? <Loader2 className="h-4 w-4 animate-spin mr-1.5" /> : <Sliders className="h-4 w-4 mr-1.5" />}Apply</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

// ─── Movements ─────────────────────────────────────────────────────────────
const MovementsTab = ({ shapes, qualities, sieves, shapeMap, qualityMap, sieveMap }) => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState({ type: '', shape_id: '', quality_id: '' });
  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      Object.entries(filter).forEach(([k, v]) => { if (v) params[k] = v; });
      const r = await diamondsApi.listMovements(params);
      setRows(r.data || []);
    } catch (e) { toast.error(e?.response?.data?.detail || 'Failed'); }
    finally { setLoading(false); }
  }, [filter]);
  useEffect(() => { load(); }, [load]);

  const typeChip = (t) => {
    if (t === 'purchase') return { label: 'Purchase', cls: 'border-sky-300 bg-sky-50 text-sky-700' };
    if (t === 'assort') return { label: 'Assort', cls: 'border-violet-300 bg-violet-50 text-violet-700' };
    if (t === 'issue') return { label: 'Issue', cls: 'border-emerald-300 bg-emerald-50 text-emerald-700' };
    return { label: 'Adjust', cls: 'border-amber-300 bg-amber-50 text-amber-700' };
  };

  return (
    <div className="space-y-3">
      <div className="flex items-center gap-2 flex-wrap">
        <Select value={filter.type || 'any'} onValueChange={v => setFilter(p => ({...p, type: v === 'any' ? '' : v}))}>
          <SelectTrigger className="w-36 h-9 text-xs" data-testid="mv-type-filter"><SelectValue placeholder="All types" /></SelectTrigger>
          <SelectContent><SelectItem value="any">All types</SelectItem>
            <SelectItem value="purchase">Purchase</SelectItem><SelectItem value="assort">Assort</SelectItem>
            <SelectItem value="issue">Issue</SelectItem><SelectItem value="adjustment">Adjustment</SelectItem>
          </SelectContent>
        </Select>
        <Select value={filter.shape_id || 'any'} onValueChange={v => setFilter(p => ({...p, shape_id: v === 'any' ? '' : v}))}>
          <SelectTrigger className="w-36 h-9 text-xs" data-testid="mv-shape-filter"><SelectValue placeholder="All shapes" /></SelectTrigger>
          <SelectContent><SelectItem value="any">All shapes</SelectItem>
            {shapes.map(s => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={filter.quality_id || 'any'} onValueChange={v => setFilter(p => ({...p, quality_id: v === 'any' ? '' : v}))}>
          <SelectTrigger className="w-36 h-9 text-xs" data-testid="mv-quality-filter"><SelectValue placeholder="All qualities" /></SelectTrigger>
          <SelectContent><SelectItem value="any">All qualities</SelectItem>
            {qualities.map(q => <SelectItem key={q.id} value={q.id}>{q.name}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>
      {loading ? <div className="flex justify-center py-14"><Loader2 className="h-5 w-5 animate-spin text-sky-600" /></div> :
       <div className="rounded-xl border bg-white dark:bg-neutral-900 overflow-hidden divide-y">
         {rows.length === 0 ? <div className="p-8 text-center text-sm text-muted-foreground">No movements</div> :
          rows.map(m => {
            const chip = typeChip(m.type);
            return (
              <div key={m.id} className="px-3 md:px-5 py-2.5 flex items-center gap-3 flex-wrap sm:flex-nowrap" data-testid={`movement-${m.id}`}>
                <Badge variant="outline" className={cn("text-[10px] flex-shrink-0", chip.cls)}>{chip.label}</Badge>
                <div className="flex-1 min-w-0">
                  <p className="text-sm">{shapeMap[m.shape_id]?.name || '?'} · {qualityMap[m.quality_id]?.name || '?'} · {sieveMap[m.sieve_id]?.name || '?'}</p>
                  <p className="text-[10px] text-muted-foreground truncate">
                    {new Date(m.created_at).toLocaleString()} · {m.created_by_name || 'system'}
                    {m.reference && ` · ${m.reference}`}
                    {m.notes && ` · ${m.notes}`}
                  </p>
                </div>
                <div className={cn("text-sm font-mono font-semibold flex-shrink-0",
                   m.direction === 'in' ? "text-emerald-600" : m.direction === 'out' ? "text-rose-600" : "text-slate-600")}>
                  {m.direction === 'out' ? '-' : m.direction === 'in' ? '+' : ''}{fmt(m.weight_carats)} ct
                </div>
              </div>
            );
          })}
       </div>}
    </div>
  );
};

// ─── Settings tab (Shapes / Qualities / Sieves / Benchmarks) ─────────────
const SettingsTab = ({ shapes, qualities, sieves, benchmarks, reload, shapeMap, qualityMap, sieveMap }) => {
  const [subtab, setSubtab] = useState('shapes');
  const { user } = useAuth();
  const isSuperAdmin = user?.role === 'super_admin';
  return (
    <div className="space-y-3" data-testid="diamond-settings">
      <Tabs value={subtab} onValueChange={setSubtab}>
        <TabsList className={cn("w-full max-w-xl grid h-auto", isSuperAdmin ? "grid-cols-5" : "grid-cols-4")}>
          <TabsTrigger value="shapes" className="py-1.5 text-xs" data-testid="sub-shapes">Shapes</TabsTrigger>
          <TabsTrigger value="qualities" className="py-1.5 text-xs" data-testid="sub-qualities">Qualities</TabsTrigger>
          <TabsTrigger value="sieves" className="py-1.5 text-xs" data-testid="sub-sieves">Sieves</TabsTrigger>
          <TabsTrigger value="benchmarks" className="py-1.5 text-xs" data-testid="sub-benchmarks">Benchmarks</TabsTrigger>
          {isSuperAdmin && <TabsTrigger value="danger" className="py-1.5 text-xs text-rose-700" data-testid="sub-danger">Danger</TabsTrigger>}
        </TabsList>
        <TabsContent value="shapes" className="mt-3">
          <SimpleCRUD items={shapes} label="Shape" api={diamondsApi.createShape} apiUpdate={diamondsApi.updateShape} apiDelete={diamondsApi.deleteShape} onChange={reload} testidPrefix="shape" />
        </TabsContent>
        <TabsContent value="qualities" className="mt-3">
          <SimpleCRUD items={qualities} label="Quality" api={diamondsApi.createQuality} apiUpdate={diamondsApi.updateQuality} apiDelete={diamondsApi.deleteQuality} onChange={reload} testidPrefix="quality" />
        </TabsContent>
        <TabsContent value="sieves" className="mt-3">
          <SieveCRUD sieves={sieves} shapes={shapes} shapeMap={shapeMap} onChange={reload} />
        </TabsContent>
        <TabsContent value="benchmarks" className="mt-3">
          <BenchmarkCRUD {...{shapes, qualities, sieves, benchmarks, shapeMap, qualityMap, sieveMap, reload}} />
        </TabsContent>
        {isSuperAdmin && (
          <TabsContent value="danger" className="mt-3">
            <DangerZone onWipe={reload} />
          </TabsContent>
        )}
      </Tabs>
    </div>
  );
};

const DangerZone = ({ onWipe }) => {
  const [mode, setMode] = useState(null); // 'txn' | 'all'
  const [confirmText, setConfirmText] = useState('');
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    if (confirmText !== 'WIPE') { toast.error('Type WIPE to confirm'); return; }
    setBusy(true);
    try {
      const r = await diamondsApi.wipe({ confirm: 'WIPE', include_config: mode === 'all' });
      const total = Object.values(r.data?.deleted || {}).reduce((a, b) => a + b, 0);
      toast.success(`Wiped ${total} records`);
      setMode(null); setConfirmText('');
      onWipe?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed');
    } finally { setBusy(false); }
  };
  return (
    <div className="p-4 rounded-xl border border-rose-200 bg-rose-50/40 dark:bg-rose-500/5 space-y-3" data-testid="danger-zone">
      <div className="flex items-start gap-2">
        <AlertTriangle className="h-5 w-5 text-rose-600 flex-shrink-0 mt-0.5" />
        <div>
          <p className="text-sm font-semibold text-rose-800">Danger zone</p>
          <p className="text-xs text-rose-700/80">Bulk-delete diamond data. This cannot be undone.</p>
        </div>
      </div>
      <div className="grid gap-2 md:grid-cols-2">
        <div className="p-3 rounded-lg border bg-white dark:bg-neutral-900 space-y-2">
          <p className="text-sm font-semibold">Wipe transactional data</p>
          <p className="text-[11px] text-muted-foreground">Deletes every lot, assorted bucket, benchmark and movement. Keeps your Shapes, Qualities and Sieves catalogue.</p>
          <Button size="sm" variant="outline" className="w-full h-9 border-rose-300 text-rose-700 hover:bg-rose-50" onClick={() => setMode('txn')} data-testid="wipe-txn-btn">
            <Trash2 className="h-3.5 w-3.5 mr-1.5" /> Wipe transactional data
          </Button>
        </div>
        <div className="p-3 rounded-lg border bg-white dark:bg-neutral-900 space-y-2">
          <p className="text-sm font-semibold">Wipe everything (incl. config)</p>
          <p className="text-[11px] text-muted-foreground">Deletes everything above PLUS every Shape, Quality and Sieve. Defaults will re-seed on next reload.</p>
          <Button size="sm" variant="outline" className="w-full h-9 border-rose-400 bg-rose-600 text-white hover:bg-rose-700 hover:text-white" onClick={() => setMode('all')} data-testid="wipe-all-btn">
            <Trash2 className="h-3.5 w-3.5 mr-1.5" /> Wipe everything
          </Button>
        </div>
      </div>

      {mode && (
        <Dialog open onOpenChange={(v) => !busy && !v && setMode(null)}>
          <DialogContent className="max-w-md" data-testid="wipe-confirm-dialog">
            <DialogHeader>
              <DialogTitle className="text-rose-700 flex items-center gap-2">
                <AlertTriangle className="h-5 w-5" /> Confirm {mode === 'all' ? 'FULL WIPE' : 'transactional wipe'}
              </DialogTitle>
              <DialogDescription>
                {mode === 'all'
                  ? "This will permanently delete every lot, bucket, benchmark, movement AND all shape/quality/sieve config. Defaults will re-seed on next load."
                  : "This will permanently delete every lot, bucket, benchmark and movement. Your shape/quality/sieve config is safe."}
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-2">
              <Label className="text-xs">Type <strong className="font-mono">WIPE</strong> to confirm</Label>
              <Input value={confirmText} onChange={(e) => setConfirmText(e.target.value)}
                     placeholder="WIPE" className="h-10 font-mono" autoFocus data-testid="wipe-confirm-input" />
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setMode(null)} disabled={busy}>Cancel</Button>
              <Button onClick={submit} disabled={busy || confirmText !== 'WIPE'}
                      className="bg-rose-600 hover:bg-rose-700 text-white" data-testid="wipe-confirm-submit">
                {busy ? <Loader2 className="h-4 w-4 animate-spin mr-1.5" /> : <Trash2 className="h-4 w-4 mr-1.5" />}
                Wipe now
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
};

const SimpleCRUD = ({ items, label, api, apiUpdate, apiDelete, onChange, testidPrefix }) => {
  const [name, setName] = useState('');
  const [busy, setBusy] = useState(false);
  const add = async () => {
    if (!name.trim()) return;
    setBusy(true);
    try { await api({ name, sort_order: items.length, active: true }); setName(''); onChange(); toast.success('Added'); }
    catch (e) { toast.error(e?.response?.data?.detail || 'Failed'); }
    finally { setBusy(false); }
  };
  const toggle = async (it) => {
    try { await apiUpdate(it.id, { name: it.name, sort_order: it.sort_order || 0, active: !it.active }); onChange(); }
    catch (e) { toast.error(e?.response?.data?.detail || 'Failed'); }
  };
  const rename = async (it) => {
    const v = window.prompt(`Rename ${label}`, it.name);
    if (!v || v === it.name) return;
    try { await apiUpdate(it.id, { name: v, sort_order: it.sort_order || 0, active: it.active }); onChange(); }
    catch (e) { toast.error(e?.response?.data?.detail || 'Failed'); }
  };
  const del = async (it) => {
    if (!window.confirm(`Delete ${it.name}?`)) return;
    try { await apiDelete(it.id); onChange(); toast.success('Deleted'); }
    catch (e) { toast.error(e?.response?.data?.detail || 'Failed'); }
  };
  return (
    <div className="space-y-2">
      <div className="flex gap-2">
        <Input value={name} onChange={e => setName(e.target.value)} placeholder={`New ${label} name`} className="h-10" data-testid={`${testidPrefix}-input`} />
        <Button onClick={add} disabled={busy || !name.trim()} className="h-10 bg-sky-600 hover:bg-sky-700 text-white" data-testid={`${testidPrefix}-add`}>
          <Plus className="h-4 w-4 mr-1" /> Add
        </Button>
      </div>
      <div className="rounded-xl border bg-white dark:bg-neutral-900 divide-y">
        {items.map(it => (
          <div key={it.id} className="px-3 py-2 flex items-center gap-2" data-testid={`${testidPrefix}-row-${it.id}`}>
            <span className={cn("text-sm flex-1", !it.active && "text-muted-foreground line-through")}>{it.name}</span>
            <Button size="sm" variant="ghost" onClick={() => rename(it)} className="h-7 px-2"><Pencil className="h-3.5 w-3.5" /></Button>
            <Button size="sm" variant="ghost" onClick={() => toggle(it)} className="h-7 text-xs">{it.active ? 'Archive' : 'Reactivate'}</Button>
            <Button size="sm" variant="ghost" onClick={() => del(it)} className="h-7 px-2 text-rose-600 hover:bg-rose-50"><Trash2 className="h-3.5 w-3.5" /></Button>
          </div>
        ))}
      </div>
    </div>
  );
};

const SieveCRUD = ({ sieves, shapes, shapeMap, onChange }) => {
  const [f, setF] = useState({ shape_id: '', kind: 'assorted', name: '', mm: '', weight_per_stone_carats: '', sort_order: 0 });
  const [busy, setBusy] = useState(false);
  const add = async () => {
    if (!f.shape_id || !f.name) { toast.error('Shape and name required'); return; }
    setBusy(true);
    try {
      await diamondsApi.createSieve({
        ...f, active: true,
        mm: f.mm ? parseFloat(f.mm) : null,
        weight_per_stone_carats: f.weight_per_stone_carats ? parseCarat(f.weight_per_stone_carats) : null,
      });
      setF(p => ({...p, name: '', mm: '', weight_per_stone_carats: ''}));
      onChange(); toast.success('Added');
    } catch (e) { toast.error(e?.response?.data?.detail || 'Failed'); }
    finally { setBusy(false); }
  };
  const del = async (it) => {
    if (!window.confirm(`Delete sieve ${it.name}?`)) return;
    try { await diamondsApi.deleteSieve(it.id); onChange(); toast.success('Deleted'); }
    catch (e) { toast.error(e?.response?.data?.detail || 'Failed'); }
  };

  // Persist a reordered list of sieves (same shape+kind group). Rewrites
  // every row's sort_order to its new 0-based index.
  const persistOrder = async (ordered) => {
    try {
      await Promise.all(ordered.map((s, idx) => diamondsApi.updateSieve(s.id, {
        shape_id: s.shape_id, kind: s.kind, name: s.name,
        mm: s.mm ?? null,
        weight_per_stone_carats: s.weight_per_stone_carats ?? null,
        sort_order: idx, active: s.active !== false,
      })));
      onChange();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Failed to reorder'); }
  };
  const moveWithin = (group, sieve, dir) => {
    const sorted = [...group].sort((a, b) => (a.sort_order || 0) - (b.sort_order || 0));
    const idx = sorted.findIndex(s => s.id === sieve.id);
    const next = idx + dir;
    if (idx < 0 || next < 0 || next >= sorted.length) return;
    const copy = sorted.slice();
    [copy[idx], copy[next]] = [copy[next], copy[idx]];
    persistOrder(copy);
  };

  // ── Drag-and-drop reorder within a group ───────────────────────────
  // `drag` holds { groupKey, id } for the row being dragged.
  // `hoverKey` = "groupKey|targetId" so the drop indicator can render.
  const [drag, setDrag] = useState(null);
  const [hoverKey, setHoverKey] = useState(null);
  const onDragStart = (groupKey, id) => (e) => {
    setDrag({ groupKey, id });
    e.dataTransfer.effectAllowed = 'move';
    try { e.dataTransfer.setData('text/plain', id); } catch (_err) { /* Safari */ }
  };
  const onDragOverRow = (groupKey, targetId) => (e) => {
    if (!drag || drag.groupKey !== groupKey || drag.id === targetId) return;
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    const key = `${groupKey}|${targetId}`;
    if (hoverKey !== key) setHoverKey(key);
  };
  const onDropRow = (groupKey, rows, targetId) => (e) => {
    if (!drag || drag.groupKey !== groupKey || drag.id === targetId) {
      setDrag(null); setHoverKey(null); return;
    }
    e.preventDefault();
    const sorted = [...rows].sort((a, b) => (a.sort_order || 0) - (b.sort_order || 0));
    const fromIdx = sorted.findIndex(s => s.id === drag.id);
    const toIdx = sorted.findIndex(s => s.id === targetId);
    if (fromIdx < 0 || toIdx < 0) { setDrag(null); setHoverKey(null); return; }
    const copy = sorted.slice();
    const [moved] = copy.splice(fromIdx, 1);
    copy.splice(toIdx, 0, moved);
    setDrag(null); setHoverKey(null);
    persistOrder(copy);
  };
  const onDragEnd = () => { setDrag(null); setHoverKey(null); };

  // Group sieves by (shape, kind) for reorder scope
  const groups = [];
  shapes.forEach(sh => {
    ['assorted', 'unassorted'].forEach(kind => {
      const rows = sieves.filter(s => s.shape_id === sh.id && s.kind === kind)
                          .sort((a, b) => (a.sort_order || 0) - (b.sort_order || 0));
      if (rows.length) groups.push({ shape: sh, kind, rows });
    });
  });

  return (
    <div className="space-y-3">
      <div className="p-3 rounded-xl border bg-slate-50 dark:bg-neutral-800/50 space-y-2">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
          <Select value={f.shape_id} onValueChange={v => setF(p => ({...p, shape_id: v}))}>
            <SelectTrigger className="h-9 text-xs" data-testid="sieve-shape"><SelectValue placeholder="Shape" /></SelectTrigger>
            <SelectContent>{shapes.map(s => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}</SelectContent>
          </Select>
          <Select value={f.kind} onValueChange={v => setF(p => ({...p, kind: v}))}>
            <SelectTrigger className="h-9 text-xs" data-testid="sieve-kind"><SelectValue /></SelectTrigger>
            <SelectContent><SelectItem value="assorted">Assorted range</SelectItem><SelectItem value="unassorted">Unassorted group</SelectItem></SelectContent>
          </Select>
          <Input value={f.name} onChange={e => setF(p => ({...p, name: e.target.value}))} placeholder="Name (00-0)" className="h-9 text-xs" data-testid="sieve-name" />
          <Input type="number" step="0.001" value={f.weight_per_stone_carats} onChange={e => setF(p => ({...p, weight_per_stone_carats: e.target.value}))} placeholder="ct/pc" className="h-9 text-xs font-mono" data-testid="sieve-wt" />
        </div>
        <div className="grid grid-cols-1 gap-2">
          <Input type="number" step="0.01" value={f.mm} onChange={e => setF(p => ({...p, mm: e.target.value}))} placeholder="mm size" className="h-9 text-xs" data-testid="sieve-mm" />
        </div>
        <Button onClick={add} disabled={busy} className="h-9 bg-sky-600 hover:bg-sky-700 text-white" data-testid="sieve-add"><Plus className="h-4 w-4 mr-1" /> Add sieve</Button>
      </div>

      <p className="text-[11px] text-muted-foreground px-1">
        Drag the <GripVertical className="inline h-3 w-3 -mt-0.5" /> handle to reorder sieves within a shape group (or use ↑/↓). The Assorted-tab matrix uses this order for its columns.
      </p>

      {groups.length === 0 && (
        <div className="p-6 text-center text-xs text-muted-foreground border border-dashed rounded-xl">
          No sieves configured yet.
        </div>
      )}

      {groups.map(g => {
        const groupKey = `${g.shape.id}-${g.kind}`;
        return (
        <div key={groupKey} className="rounded-xl border bg-white dark:bg-neutral-900" data-testid={`sieve-group-${g.shape.id}-${g.kind}`}>
          <div className="px-3 py-2 border-b bg-slate-50/60 dark:bg-neutral-800/40 flex items-center gap-2">
            <span className="text-sm font-semibold">{g.shape.name}</span>
            <Badge variant="outline" className="text-[10px]">{g.kind}</Badge>
            <span className="ml-auto text-[10px] text-muted-foreground">{g.rows.length} sieve{g.rows.length === 1 ? '' : 's'}</span>
          </div>
          <div className="divide-y">
            {g.rows.map((s, idx) => {
              const isDragging = drag?.groupKey === groupKey && drag?.id === s.id;
              const isHovered = hoverKey === `${groupKey}|${s.id}`;
              return (
              <div key={s.id}
                    onDragOver={onDragOverRow(groupKey, s.id)}
                    onDrop={onDropRow(groupKey, g.rows, s.id)}
                    onDragEnd={onDragEnd}
                    className={cn(
                      "px-3 py-2 flex items-center gap-2 flex-wrap sm:flex-nowrap transition-colors",
                      isDragging && "opacity-40 bg-sky-50/50",
                      isHovered && "bg-sky-50 border-l-2 border-l-sky-500"
                    )}
                    data-testid={`sieve-row-${s.id}`}>
                <div
                  draggable
                  onDragStart={onDragStart(groupKey, s.id)}
                  className="flex items-center justify-center h-8 w-5 text-slate-400 hover:text-sky-700 cursor-grab active:cursor-grabbing"
                  title="Drag to reorder"
                  data-testid={`sieve-drag-${s.id}`}
                >
                  <GripVertical className="h-4 w-4" />
                </div>
                <div className="flex flex-col">
                  <button type="button" onClick={() => moveWithin(g.rows, s, -1)} disabled={idx === 0}
                          className="h-4 w-6 flex items-center justify-center text-slate-400 hover:text-sky-700 disabled:opacity-20 disabled:cursor-not-allowed"
                          data-testid={`sieve-up-${s.id}`} title="Move up">
                    <ChevronUp className="h-4 w-4" />
                  </button>
                  <button type="button" onClick={() => moveWithin(g.rows, s, 1)} disabled={idx === g.rows.length - 1}
                          className="h-4 w-6 flex items-center justify-center text-slate-400 hover:text-sky-700 disabled:opacity-20 disabled:cursor-not-allowed"
                          data-testid={`sieve-down-${s.id}`} title="Move down">
                    <ChevronDown className="h-4 w-4" />
                  </button>
                </div>
                <span className="text-xs font-mono text-muted-foreground w-6 text-center">{idx + 1}</span>
                <span className="text-sm font-medium">{s.name}</span>
                <span className="text-[10px] text-muted-foreground">
                  {s.mm != null && `${s.mm} mm`}
                  {s.weight_per_stone_carats && ` · ${fmt(s.weight_per_stone_carats)} ct/pc`}
                </span>
                <Button size="sm" variant="ghost" onClick={() => del(s)} className="h-7 px-2 ml-auto text-rose-600 hover:bg-rose-50"><Trash2 className="h-3.5 w-3.5" /></Button>
              </div>
              );
            })}
          </div>
        </div>
        );
      })}
    </div>
  );
};

const BenchmarkCRUD = ({ shapes, qualities, sieves, benchmarks, shapeMap, qualityMap, sieveMap, reload }) => {
  const [f, setF] = useState({ shape_id: '', quality_id: '', sieve_id: '', min_carats: '', max_carats: '' });
  const [busy, setBusy] = useState(false);
  const assSieves = sieves.filter(s => s.shape_id === f.shape_id && s.kind === 'assorted');
  const submit = async () => {
    if (!f.shape_id || !f.quality_id || !f.sieve_id) { toast.error('Pick shape, quality & sieve'); return; }
    setBusy(true);
    try {
      await diamondsApi.upsertBenchmark({ ...f,
        min_carats: parseCarat(f.min_carats),
        max_carats: parseCarat(f.max_carats),
      });
      setF({ shape_id: '', quality_id: '', sieve_id: '', min_carats: '', max_carats: '' });
      reload(); toast.success('Benchmark saved');
    } catch (e) { toast.error(e?.response?.data?.detail || 'Failed'); }
    finally { setBusy(false); }
  };
  const del = async (b) => {
    try { await diamondsApi.deleteBenchmark(b.id); reload(); }
    catch (e) { toast.error(e?.response?.data?.detail || 'Failed'); }
  };
  return (
    <div className="space-y-3">
      <div className="p-3 rounded-xl border bg-slate-50 dark:bg-neutral-800/50 grid grid-cols-2 md:grid-cols-5 gap-2">
        <Select value={f.shape_id} onValueChange={v => setF(p => ({...p, shape_id: v, sieve_id: ''}))}>
          <SelectTrigger className="h-9 text-xs" data-testid="bm-shape"><SelectValue placeholder="Shape" /></SelectTrigger>
          <SelectContent>{shapes.map(s => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}</SelectContent>
        </Select>
        <Select value={f.quality_id} onValueChange={v => setF(p => ({...p, quality_id: v}))}>
          <SelectTrigger className="h-9 text-xs" data-testid="bm-quality"><SelectValue placeholder="Quality" /></SelectTrigger>
          <SelectContent>{qualities.map(q => <SelectItem key={q.id} value={q.id}>{q.name}</SelectItem>)}</SelectContent>
        </Select>
        <Select value={f.sieve_id} onValueChange={v => setF(p => ({...p, sieve_id: v}))} disabled={!f.shape_id}>
          <SelectTrigger className="h-9 text-xs" data-testid="bm-sieve"><SelectValue placeholder="Sieve" /></SelectTrigger>
          <SelectContent>{assSieves.map(s => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}</SelectContent>
        </Select>
        <Input type="number" step="0.001" value={f.min_carats} onChange={e => setF(p => ({...p, min_carats: e.target.value}))} placeholder="Min ct" className="h-9 text-xs font-mono" data-testid="bm-min" />
        <Input type="number" step="0.001" value={f.max_carats} onChange={e => setF(p => ({...p, max_carats: e.target.value}))} placeholder="Max ct" className="h-9 text-xs font-mono" data-testid="bm-max" />
      </div>
      <Button onClick={submit} disabled={busy} className="h-9 bg-sky-600 hover:bg-sky-700 text-white" data-testid="bm-add"><Plus className="h-4 w-4 mr-1" /> Set benchmark</Button>
      <div className="rounded-xl border bg-white dark:bg-neutral-900 divide-y">
        {benchmarks.map(b => (
          <div key={b.id} className="px-3 py-2 flex items-center gap-2" data-testid={`bm-row-${b.id}`}>
            <span className="text-sm flex-1">{shapeMap[b.shape_id]?.name} · {qualityMap[b.quality_id]?.name} · {sieveMap[b.sieve_id]?.name}</span>
            <span className="text-xs font-mono">{fmt(b.min_carats)} – {fmt(b.max_carats)} ct</span>
            <Button size="sm" variant="ghost" onClick={() => del(b)} className="h-7 px-2 text-rose-600 hover:bg-rose-50"><Trash2 className="h-3.5 w-3.5" /></Button>
          </div>
        ))}
      </div>
    </div>
  );
};

export default Diamonds;
